import React from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

import { apiRequest } from "@mhj/shared/api";
import { Card, StatusPill, buttonClass } from "../lib/ui";
import { docLabel } from "../lib/docs";
import type { Application } from "../lib/types";

export default function Dashboard() {
  const { data, isLoading } = useQuery({
    queryKey: ["empowerment", "me"],
    queryFn: () =>
      apiRequest<{ application: Application | null; history: Application[] }>("/empowerment/me"),
  });

  if (isLoading) return <Card><p className="text-slate-500">Loading…</p></Card>;

  const application = data?.application ?? null;
  const history = data?.history ?? [];

  return (
    <div className="space-y-5">
      {application ? (
        <Card>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="text-xs uppercase tracking-wide text-slate-500">
                {application.applicationNo ? "Application ID" : "Not submitted yet"}
              </p>
              <p className="text-xl font-bold text-slate-900">
                {application.applicationNo ?? "Draft"}
              </p>
            </div>
            <StatusPill status={application.status} />
          </div>

          <dl className="mt-4 grid gap-3 text-sm sm:grid-cols-2">
            <div>
              <dt className="text-slate-500">Ward</dt>
              <dd className="font-medium text-slate-900">{application.ward}</dd>
            </div>
            <div>
              <dt className="text-slate-500">Submitted</dt>
              <dd className="font-medium text-slate-900">
                {application.submittedAt
                  ? new Date(application.submittedAt).toLocaleDateString()
                  : "Not yet"}
              </dd>
            </div>
            <div>
              <dt className="text-slate-500">Documents</dt>
              <dd className="font-medium text-slate-900">
                {application.documents.length} uploaded
              </dd>
            </div>
          </dl>

          {application.status === "rejected" && application.rejectionReason && (
            <div className="mt-4 rounded-2xl bg-red-50 p-4 text-sm text-red-900">
              <p className="font-medium">Why</p>
              <p className="mt-1">{application.rejectionReason}</p>
            </div>
          )}

          {(application.awards ?? []).length > 0 && (
            <div className="mt-4 space-y-2">
              <p className="text-sm font-semibold text-slate-900">What you have been awarded</p>
              {(application.awards ?? []).map((award) => (
                <div key={award.id} className="rounded-2xl bg-emerald-50 p-4 text-sm">
                  <p className="font-medium text-emerald-900">
                    {award.type === "cash"
                      ? `₦${Number(award.amount ?? 0).toLocaleString()}`
                      : award.type === "equipment"
                        ? award.itemDescription
                        : "Training place"}
                  </p>
                  {award.centre && (
                    <p className="mt-1 text-emerald-800">Report to {award.centre}</p>
                  )}
                  {award.scheduledDate && (
                    <p className="text-emerald-800">
                      On {new Date(award.scheduledDate).toLocaleDateString()}
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}

          {!application.submittedAt && (
            <Link to="/apply" className={`${buttonClass} mt-4`}>
              Finish my application
            </Link>
          )}
        </Card>
      ) : (
        <Card title="You have not applied yet">
          <p className="text-slate-600">
            Applying takes about ten minutes and your answers are saved as you go.
          </p>
          <Link to="/apply" className={`${buttonClass} mt-4`}>
            Start my application
          </Link>
        </Card>
      )}

      {history.length > 0 && (
        <Card title="Earlier rounds">
          <div className="space-y-2">
            {history.map((past) => (
              <div
                key={past.id}
                className="flex items-center justify-between gap-3 rounded-2xl bg-slate-50 px-4 py-3"
              >
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-slate-900">
                    {past.cycleName ?? "Previous round"}
                  </p>
                  <p className="text-xs text-slate-500">{past.applicationNo}</p>
                </div>
                <StatusPill status={past.status} />
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}
